import { Slider } from '@components/Slider'
import { ICourseModulesNodes } from '@dtos/Course'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Stack,
  StackProps,
  isWeb,
  useMedia,
  useWindowDimensions,
} from 'tamagui'
import { ModuleSliderTitle } from './ModuleSlidertitle'
import { useEnrollment } from '@hooks/useEnrollment'
import { LessonCard } from './LessonCard'
import { useCourseContents } from '@hooks/useCourseContents'
import { useLinearProgressGate } from '@hooks/useLinearProgressGate'
import { InView } from '@components/IntersectionObserver'
interface IModuleContainerProps extends StackProps {
  module: ICourseModulesNodes
  isLastModuleOfData?: boolean
  displayBorder?: boolean
  onLoadMoreCallBack?: () => void
  parentCourseModuleId?: number
  isLastSubmodule?: boolean
  fatherHasContent?: boolean
  isFirstModuleOfCourse?: boolean
  isSubmodulesList?: boolean
  isLastOfSubmodulesList?: boolean
  showLessonTitle?: boolean
  /** Sem o respiro de cima/baixo que separa módulos empilhados na tela do curso. */
  flushTop?: boolean
  flushBottom?: boolean
}

export function ModuleContainer({
  module,
  isLastModuleOfData,
  displayBorder,
  onLoadMoreCallBack,
  parentCourseModuleId,
  isLastSubmodule = false,
  fatherHasContent = false,
  isFirstModuleOfCourse = false,
  isSubmodulesList = false,
  isLastOfSubmodulesList = false,
  showLessonTitle = true,
  flushTop = false,
  flushBottom = false,
  ...rest
}: IModuleContainerProps) {
  const [moduleState, setModulesState] = useState<ICourseModulesNodes>(module)
  const { getCourseContents } = useCourseContents()
  const { enrollment } = useEnrollment()
  const { isContentBlocked } = useLinearProgressGate()
  const [isVisible, setIsVisible] = useState(false)
  // On web the InView trigger is unreliable on fast scroll (modules stayed as
  // empty placeholders), so render eagerly there; keep lazy render on native.
  const [hasBeenVisible, setHasBeenVisible] = useState(
    isFirstModuleOfCourse || isWeb,
  )
  const [loadedMoreModules, setLoadedMoreModules] = useState(false)

  const hasMoreLessons =
    moduleState.courseContents.nodes.length <
    (module.courseContentsCount ?? Infinity)
  const moduleContainerRef = useRef<HTMLDivElement>(null)
  const isLoadingMoreRef = useRef(false)
  const requestedOffsetsRef = useRef<Set<number>>(new Set([0]))
  const { width } = useWindowDimensions()
  const media = useMedia()

  const sliderSpacing = useMemo(() => {
    if (!media.gtSm) return 16
    return 24
  }, [media.gtSm])

  const containerPadding = useMemo(() => {
    if (media.gtMd) return 48
    return media.gtSm ? 24 : 16
  }, [media.gtMd, media.gtSm])

  const cardWidth = useMemo(() => {
    if (media.gtMd) return 407
    return media.gtSm ? 292 : 300
  }, [media.gtMd, media.gtSm])

  const spaceBetweenCards = useMemo(() => {
    return media.gtSm ? 20 : 5
  }, [media.gtSm])

  const perView =
    (width - containerPadding * 2) / (cardWidth + spaceBetweenCards)

  const placeholderHeight = useMemo(() => {
    const cardMinHeight = media.gtSm ? 300 : 206
    return cardMinHeight + 80
  }, [media.gtSm])

  const findLessonProgress = useCallback(
    (moduleId: number) => {
      return enrollment?.lessonsCompletedPerModule[moduleId] || 0
    },
    [enrollment?.lessonsCompletedPerModule],
  )

  const availableContents = useMemo(
    () =>
      moduleState.courseContents.nodes.reduce(
        (acc, content) => (content.available === false ? acc : acc + 1),
        0,
      ),
    [moduleState.courseContents.nodes],
  )

  const displaySliderControls = availableContents > perView

  useEffect(() => {
    if (isVisible && isLastModuleOfData && !loadedMoreModules) {
      setLoadedMoreModules(true)
      onLoadMoreCallBack?.()
    }
  }, [isVisible, isLastModuleOfData, loadedMoreModules, onLoadMoreCallBack])

  const loadMoreLessons = useCallback(async () => {
    if (!moduleState) return
    // Evita requests concorrentes e refetch do mesmo offset. Durante scroll
    // rápido o offset (tamanho atual de nodes) fica stale entre o fim de uma
    // request e o re-render, duplicando a mesma página.
    if (isLoadingMoreRef.current) return
    const offset = moduleState.courseContents.nodes.length
    if (requestedOffsetsRef.current.has(offset)) return
    isLoadingMoreRef.current = true
    requestedOffsetsRef.current.add(offset)
    try {
      const dataCourseContents = await getCourseContents?.(
        `${moduleState.id}`,
        offset,
      )
      const newModuleLessons =
        dataCourseContents?.course.courseModules?.nodes[0]?.courseContents
          ?.nodes

      if (newModuleLessons?.length) {
        setModulesState((prevState) => {
          const existingIds = new Set(
            prevState.courseContents.nodes.map((node) => node.id),
          )
          const freshLessons = newModuleLessons.filter(
            (node) => !existingIds.has(node.id),
          )
          if (freshLessons.length === 0) return prevState
          return {
            ...prevState,
            courseContents: {
              nodes: [...prevState.courseContents.nodes, ...freshLessons],
            },
          }
        })
      }
    } finally {
      isLoadingMoreRef.current = false
    }
  }, [moduleState, getCourseContents])
  const checkInView = useCallback(
    (inView: boolean) => {
      if (inView && !hasBeenVisible) {
        setHasBeenVisible(true)
      }

      if (!isVisible && isLastModuleOfData && inView && !isSubmodulesList) {
        setIsVisible(true)
      }

      if (isSubmodulesList && isLastOfSubmodulesList && inView) {
        onLoadMoreCallBack?.()
      }
    },
    [
      hasBeenVisible,
      isVisible,
      isLastModuleOfData,
      isSubmodulesList,
      isLastOfSubmodulesList,
      onLoadMoreCallBack,
    ],
  )

  if (!enrollment) return null

  return (
    <InView onChange={checkInView} threshold={0.1}>
      <Stack
        {...rest}
        ref={moduleContainerRef}
        gap={40}
        borderTopColor={displayBorder ? 'transparent' : '$border-subtle'}
        borderTopWidth={1}
        paddingTop={fatherHasContent || flushTop ? 0 : '$spacing-inset-lg'}
        paddingBottom={
          isLastSubmodule && !flushBottom ? '$spacing-inset-lg' : 0
        }
      >
        {hasBeenVisible ? (
          <Stack marginHorizontal={-containerPadding}>
            <Slider
              slidesTotalNumber={moduleState.courseContents.nodes.length}
              slidesSpacing={sliderSpacing}
              slidesPerView={perView}
              contentInset={containerPadding}
              sliderTitle={
                <ModuleSliderTitle
                  isSubmodule={!module.courseModules}
                  moduleName={module.name}
                  totalLessons={module.courseContentsCount}
                  numberOfLessons={module.courseContents?.nodes.length}
                  lessonsCompletedPerModule={findLessonProgress(module.id)}
                />
              }
              displaySliderControls={displaySliderControls}
              loadMoreContent={loadMoreLessons}
              hasMore={hasMoreLessons}
            >
              {moduleState.courseContents.nodes.map((item) => {
                return (
                  <LessonCard
                    key={item.id}
                    width={cardWidth}
                    enrollment={enrollment}
                    lessonData={item}
                    moduleId={moduleState.id}
                    hasContentLesson={
                      !!item.lesson.media || !!item.lesson.activity
                    }
                    parentCourseModuleId={parentCourseModuleId}
                    isLessonBlockedByLinearProgress={isContentBlocked(item.id)}
                    showTitle={showLessonTitle}
                  />
                )
              })}
            </Slider>
          </Stack>
        ) : (
          <Stack height={placeholderHeight}>
            <ModuleSliderTitle
              isSubmodule={!module.courseModules}
              moduleName={module.name}
              totalLessons={module.courseContentsCount}
              numberOfLessons={module.courseContents?.nodes.length}
              lessonsCompletedPerModule={findLessonProgress(module.id)}
            />
          </Stack>
        )}
      </Stack>
    </InView>
  )
}
